<template>
  <div class="w-full h-full py-6">
    <!-- Настройки урока -->
    <div class="bg-gray-50 rounded-2xl p-4 mb-6">
      <div class="flex justify-between items-center mb-4">
        <div class="text-xl font-semibold">Настройки урока</div>
        <button
          v-if="lessonData.id"
          @click="$emit('deleteLesson', lessonData.id)"
          class="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
        >
          Удалить урок
        </button>
      </div>
      
      <div>
        <label class="block text-gray-700 text-sm font-medium mb-2">
          Название урока
        </label>
        <input
          type="text"
          v-model="lessonData.title"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#574AEA]"
          placeholder="Введите название урока"
          @input="onUpdate"
        />
      </div>
    </div>

    <!-- Редактор контента -->
    <div class="bg-gray-50 rounded-2xl p-4">
      <div class="text-lg font-semibold mb-4">Содержание урока</div>
      
      <div class="mb-4 max-w-full">
        <label class="block text-gray-700 text-sm font-medium mb-2">
          Контент урока
        </label>
        <div class="max-w-full overflow-hidden">
          <UQuillEditor
            :key="editorKey"
            ref="quillEditor"
            v-model="lessonData.content"
            @update:content="onContentUpdate"
            height="500px"
          />
        </div>
      </div>
      
      <div class="text-sm text-gray-500">
        <p class="mb-2">Подсказки по форматированию:</p>
        <ul class="list-disc list-inside space-y-1">
          <li>Используйте панель инструментов для форматирования текста</li>
          <li>Создавайте заголовки различных уровней для структурирования материала</li>
          <li>Добавляйте списки, ссылки и цитаты для лучшего восприятия</li>
          <li>Используйте выделения и цвета для акцентирования важной информации</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, onMounted, computed } from 'vue'
import UQuillEditor from '@/components/ui/UQuillEditor.vue'
import Quill from 'quill'
import { usePortalStore } from '@/stores/portalStore'

const props = defineProps({
  lesson: {
    type: Object,
    required: true,
    default: () => ({
      title: '',
      content: ''
    })
  }
})

const emit = defineEmits(['update:lesson', 'deleteLesson'])

// Создаем локальную копию данных урока
const lessonData = ref({
  id: props.lesson.id,
  title: props.lesson.title || '',
  content: props.lesson.content || ''
})

// Регистрируем модули
onMounted(() => {
  // Добавляем кастомную кнопку для таблиц
  const TableButton = Quill.import('ui/icons')
  TableButton['table'] = '<svg viewBox="0 0 18 18"><rect class="ql-stroke" height="12" width="12" x="3" y="3"></rect><rect class="ql-fill" height="2" width="3" x="5" y="5"></rect><rect class="ql-fill" height="2" width="3" x="8" y="5"></rect><rect class="ql-fill" height="2" width="3" x="11" y="5"></rect><rect class="ql-fill" height="2" width="3" x="5" y="8"></rect><rect class="ql-fill" height="2" width="3" x="8" y="8"></rect><rect class="ql-fill" height="2" width="3" x="11" y="8"></rect><rect class="ql-fill" height="2" width="3" x="5" y="11"></rect><rect class="ql-fill" height="2" width="3" x="8" y="11"></rect><rect class="ql-fill" height="2" width="3" x="11" y="11"></rect></svg>'
})

// Настройки перенесены в компонент UQuillEditor
// Ключ редактора для перемонтирования при смене прав (isViewFiles)
const portalStore = usePortalStore()
const editorKey = computed(() => {
  const viewMode = portalStore.portalData?.isViewFiles ? '1' : '0'
  const lessonId = props.lesson?.id || 'new'
  return `editor-${viewMode}-${lessonId}`
})

// Обновление при изменении props
watch(() => props.lesson, (newValue) => {
  lessonData.value.id = newValue.id
  lessonData.value.title = newValue.title || ''
  lessonData.value.content = newValue.content || ''
}, { deep: true })

// Отправка обновленных данных родителю
const onUpdate = () => {
  emit('update:lesson', {
    ...props.lesson,
    title: lessonData.value.title,
    content: lessonData.value.content
  })
}

// Обработка изменения контента через Quill
const onContentUpdate = (value) => {
  lessonData.value.content = value
  onUpdate()
}
</script> 
